/**
 * Feature flags del frontend.
 *
 * **Por qué un módulo y no env vars individuales:**
 *
 * Un solo punto de verdad. Cuando un feature pase a producción, se
 * cambia el default acá (sin tocar 5 archivos) y opcionalmente se
 * promueve a env var si el equipo lo necesita configurable por
 * ambiente. Hoy las flags son simples constantes booleanas porque el
 * caso de uso es "MVP vs V2", no A/B testing.
 *
 * **Convenciones:**
 *
 * - Default `false` para todo lo que no entró al MVP — se enciende
 *   en V2 cambiando a `true` aquí o vía variable de entorno
 *   `VITE_FEATURE_<NAME>`.
 * - Cada flag debe tener el sprint que la introdujo y el sprint
 *   esperado de release en el comentario.
 * - Tests directos de los componentes gated NO deben depender de la
 *   flag; gatear solo en las páginas / containers.
 */

function envFlag(name: string, fallback: boolean): boolean {
  const raw = import.meta.env?.[name];
  if (raw === undefined || raw === null) return fallback;
  const norm = String(raw).trim().toLowerCase();
  if (norm === "1" || norm === "true" || norm === "yes" || norm === "on") {
    return true;
  }
  if (norm === "0" || norm === "false" || norm === "no" || norm === "off") {
    return false;
  }
  return fallback;
}

/**
 * Acciones de curaduría del Radar:
 * - Botones de favoritar (♥) / descartar (✘) por card.
 * - Checkbox de selección múltiple + barra de acciones bulk.
 * - Chips de filtro "♥ Favoritas", "Mostrar descartadas".
 *
 * Introducido en Sprint 8.5 + 9 · 3. Se movió a V2 (2026-05-06) por un bug
 * visual de delay en el botón ♥ (le faltaba `onMutate` optimista). Ese bug
 * quedó RESUELTO con el optimistic UI de la curaduría (PR #329) →
 * **reactivado en V2-S6 (2026-06)**: el hallazgo de Herveo confirmó que el
 * usuario real es el secretario de planeación, que necesita curar y filtrar
 * el radar, no solo verlo.
 */
export const RADAR_CURATION_ACTIONS_ENABLED: boolean = envFlag("VITE_FEATURE_RADAR_CURATION", true);

/**
 * Captura de teléfono internacional en onboarding.
 *
 * Hoy el formulario solo acepta celulares colombianos (10 dígitos
 * empezando en 3 o 12 con +57). Cuando se abra el producto fuera de
 * Colombia, esta flag enciende un selector de país + normalización
 * E.164 con `libphonenumber-js`. También cambia el copy del label
 * de teléfono.
 *
 * Decisión founder 2026-05-06: V2.
 */
export const INTERNATIONAL_PHONE_ENABLED: boolean = envFlag(
  "VITE_FEATURE_INTERNATIONAL_PHONE",
  false,
);
